import { env } from '@fim/config';
import { AppError } from '@fim/utils';

import {
  AiProviderError,
  postJson,
  readJson,
  resolveFetch,
  resolveTimeout,
} from '../internal/http.js';
import { parseSseStream } from '../internal/sse.js';
import type { AiProvider, ProviderOptions } from '../provider.js';
import type {
  AiFinishReason,
  AiModel,
  ChatChunk,
  ChatRequest,
  ChatResult,
  EmbedRequest,
  EmbedResponse,
} from '../types.js';

const DEFAULT_BASE_URL = 'https://api.openai.com/v1';
export const OPENAI_DEFAULT_MODEL = 'gpt-4o';
export const OPENAI_DEFAULT_EMBEDDING_MODEL = 'text-embedding-3-small';
const DEFAULT_MAX_TOKENS = 4096;

const OPENAI_MODELS: readonly AiModel[] = [
  {
    id: 'gpt-4o',
    provider: 'openai',
    contextWindow: 128_000,
    maxOutputTokens: 16_384,
    supportsStreaming: true,
    supportsEmbeddings: false,
  },
  {
    id: 'gpt-4o-mini',
    provider: 'openai',
    contextWindow: 128_000,
    maxOutputTokens: 16_384,
    supportsStreaming: true,
    supportsEmbeddings: false,
  },
  {
    id: 'text-embedding-3-small',
    provider: 'openai',
    contextWindow: 8_191,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsEmbeddings: true,
  },
  {
    id: 'text-embedding-3-large',
    provider: 'openai',
    contextWindow: 8_191,
    maxOutputTokens: 0,
    supportsStreaming: false,
    supportsEmbeddings: true,
  },
];

/* -------------------------------------------------------------------------- */
/* Wire shapes                                                                */
/* -------------------------------------------------------------------------- */

interface OpenAiChatMessage {
  readonly role: 'system' | 'user' | 'assistant';
  readonly content: string;
}

interface OpenAiChatRequestBody {
  model: string;
  messages: OpenAiChatMessage[];
  max_tokens?: number;
  temperature?: number;
  stream?: boolean;
  stream_options?: { include_usage: boolean };
  user?: string;
}

interface OpenAiUsage {
  readonly prompt_tokens: number;
  readonly completion_tokens: number;
  readonly total_tokens: number;
}

interface OpenAiChatResponse {
  readonly model: string;
  readonly choices: ReadonlyArray<{
    readonly message: { readonly content: string | null };
    readonly finish_reason: string | null;
  }>;
  readonly usage?: OpenAiUsage;
}

interface OpenAiChatStreamChunk {
  readonly model: string;
  readonly choices: ReadonlyArray<{
    readonly delta: { readonly content?: string | null };
    readonly finish_reason: string | null;
  }>;
  readonly usage?: OpenAiUsage | null;
}

interface OpenAiEmbeddingResponse {
  readonly model: string;
  readonly data: ReadonlyArray<{ readonly index: number; readonly embedding: number[] }>;
  readonly usage?: { readonly prompt_tokens: number; readonly total_tokens: number };
}

/**
 * OpenAI Chat Completions provider. Implements {@link AiProvider} via raw
 * `fetch` against `/chat/completions` and `/embeddings`, with SSE parsing for
 * {@link stream}.
 */
class OpenAiProvider implements AiProvider {
  readonly name = 'openai' as const;
  readonly defaultModel: string;
  readonly models = OPENAI_MODELS;

  private readonly apiKey: string;
  private readonly baseUrl: string;
  private readonly embeddingModel: string;
  private readonly timeoutMs: number;
  private readonly fetchImpl: typeof fetch;
  private readonly extraHeaders: Record<string, string>;

  constructor(options: ProviderOptions = {}) {
    const apiKey = options.apiKey ?? env.OPENAI_API_KEY;
    if (!apiKey) {
      throw new AppError('OPENAI_API_KEY is not configured', {
        code: 'AI_MISSING_API_KEY',
        statusCode: 500,
      });
    }
    this.apiKey = apiKey;
    this.baseUrl = stripTrailingSlash(options.baseUrl ?? DEFAULT_BASE_URL);
    this.defaultModel =
      options.defaultModel ?? env.OPENAI_MODEL ?? OPENAI_DEFAULT_MODEL;
    this.embeddingModel =
      options.defaultEmbeddingModel ??
      env.AI_EMBEDDING_MODEL ??
      OPENAI_DEFAULT_EMBEDDING_MODEL;
    this.timeoutMs = resolveTimeout(options);
    this.fetchImpl = resolveFetch(options);
    this.extraHeaders = { ...options.headers };
  }

  async chat(request: ChatRequest): Promise<string> {
    return (await this.chatVerbose(request)).content;
  }

  async chatVerbose(request: ChatRequest): Promise<ChatResult> {
    const response = await postJson({
      provider: this.name,
      url: `${this.baseUrl}/chat/completions`,
      headers: this.headers(),
      body: this.buildChatBody(request, false),
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
      signal: request.signal,
    });

    const data = await readJson<OpenAiChatResponse>(response);
    const choice = data.choices[0];

    return {
      content: choice?.message.content ?? '',
      model: data.model,
      finishReason: mapFinishReason(choice?.finish_reason ?? null),
      usage: data.usage
        ? {
            inputTokens: data.usage.prompt_tokens,
            outputTokens: data.usage.completion_tokens,
            totalTokens: data.usage.total_tokens,
          }
        : undefined,
    };
  }

  async *stream(request: ChatRequest): AsyncIterable<ChatChunk> {
    const response = await postJson({
      provider: this.name,
      url: `${this.baseUrl}/chat/completions`,
      headers: this.headers(),
      body: this.buildChatBody(request, true),
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
      signal: request.signal,
    });

    if (!response.body) {
      throw new AiProviderError({
        provider: this.name,
        status: response.status,
        message: 'OpenAI stream response had no body',
      });
    }

    let finishReason: AiFinishReason = 'unknown';
    let usage: OpenAiUsage | undefined;

    for await (const sse of parseSseStream(response.body)) {
      const payload = sse.data.trim();
      if (!payload) continue;
      if (payload === '[DONE]') break;

      const chunk = safeParse<OpenAiChatStreamChunk>(payload);
      if (!chunk) continue;

      if (chunk.usage) usage = chunk.usage;

      const choice = chunk.choices[0];
      if (!choice) continue;

      if (choice.delta.content) {
        yield { delta: choice.delta.content, done: false };
      }
      if (choice.finish_reason) {
        finishReason = mapFinishReason(choice.finish_reason);
      }
    }

    yield {
      delta: '',
      done: true,
      finishReason,
      usage: usage
        ? {
            inputTokens: usage.prompt_tokens,
            outputTokens: usage.completion_tokens,
            totalTokens: usage.total_tokens,
          }
        : undefined,
    };
  }

  async embed(request: EmbedRequest): Promise<EmbedResponse> {
    const response = await postJson({
      provider: this.name,
      url: `${this.baseUrl}/embeddings`,
      headers: this.headers(),
      body: {
        model: request.model ?? this.embeddingModel,
        input: [...request.input],
        ...(request.dimensions ? { dimensions: request.dimensions } : {}),
      },
      timeoutMs: this.timeoutMs,
      fetchImpl: this.fetchImpl,
      signal: request.signal,
    });

    const data = await readJson<OpenAiEmbeddingResponse>(response);
    // Preserve input order regardless of the order the API returns rows in.
    const ordered = [...data.data].sort((a, b) => a.index - b.index);

    return {
      model: data.model,
      embeddings: ordered.map((row) => row.embedding),
      usage: data.usage
        ? {
            inputTokens: data.usage.prompt_tokens,
            totalTokens: data.usage.total_tokens,
          }
        : undefined,
    };
  }

  private headers(): Record<string, string> {
    return {
      authorization: `Bearer ${this.apiKey}`,
      ...this.extraHeaders,
    };
  }

  private buildChatBody(
    request: ChatRequest,
    stream: boolean,
  ): OpenAiChatRequestBody {
    const messages: OpenAiChatMessage[] = [];
    if (request.system) {
      messages.push({ role: 'system', content: request.system });
    }
    for (const message of request.messages) {
      // Tool messages flow only to tool-capable providers (DeepSeek); skip to stay well-typed.
      if (message.role === 'tool') continue;
      messages.push({ role: message.role, content: message.content });
    }

    const body: OpenAiChatRequestBody = {
      model: request.model ?? this.defaultModel,
      messages,
      max_tokens: request.maxTokens ?? DEFAULT_MAX_TOKENS,
    };

    if (typeof request.temperature === 'number') {
      body.temperature = request.temperature;
    }
    if (stream) {
      body.stream = true;
      // Ask OpenAI to emit a final usage-bearing chunk.
      body.stream_options = { include_usage: true };
    }
    if (request.metadata?.userId) {
      body.user = request.metadata.userId;
    }

    return body;
  }
}

/**
 * Factory for the OpenAI provider. Reads `OPENAI_API_KEY` from `@fim/config`
 * unless overridden via {@link ProviderOptions}.
 */
export const openaiProvider = (options?: ProviderOptions): AiProvider =>
  new OpenAiProvider(options);

/* -------------------------------------------------------------------------- */
/* Helpers                                                                    */
/* -------------------------------------------------------------------------- */

function mapFinishReason(reason: string | null): AiFinishReason {
  switch (reason) {
    case 'stop':
      return 'stop';
    case 'length':
      return 'length';
    case 'content_filter':
      return 'content_filter';
    case 'tool_calls':
    case 'function_call':
      return 'tool_use';
    case null:
      return 'unknown';
    default:
      return 'unknown';
  }
}

function safeParse<T>(raw: string): T | null {
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

function stripTrailingSlash(url: string): string {
  return url.endsWith('/') ? url.slice(0, -1) : url;
}
